/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { contextMenu } from 'react-contexify';
import { useSelector } from 'react-redux';

import { getTipCoordinates } from '../d3Util';
import createInfoboxMarkup from '../../../infobox/createInfoboxMarkup';
import { mapPropsToDto } from './utils';
import Toolbar from '../toolbar/Toolbar';
import VisitWebsiteContextMenu from '../../../visit-website-context-menu/VisitWebsiteContextMenu';
import NoChartsData from '../../no-charts-data/NoChartsData';

import scss from './ScatterChart.module.scss';

const CHART_MARGIN = { top: 40, right: 25, bottom: 25, left: 40 };
const labelMarginX = 5;
const labelMarginY = 5;
const wrapperHeight = 500;

export const SCATTER_CONTEXT_MENU = 'scatter/VisitWebsiteContextMenu';

const ScatterChart = ({
  data: dataProps,
  dataKeys,
  getColor,
  radius = 8,
  borderWidth = 3,
  xLabel,
  yLabel,
  reverseTrendGroup,
}) => {
  if (!dataProps.length) {
    return <NoChartsData />;
  }

  const chartRef = useRef(null);
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const zoomTransformRef = useRef(null);

  const theme = useSelector((state) => state.global.theme);
  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [width, setChartWidth] = useState(0);
  const [isShowLabels, setIsShowLabels] = useState(false);

  const height = wrapperHeight - CHART_MARGIN.top - CHART_MARGIN.bottom;

  const data = useMemo(
    () =>
      mapPropsToDto({
        data: dataProps,
        radius,
        borderWidth,
        dataKeys,
        getColor,
        theme,
        reverseTrendGroup,
      }),
    [dataProps, radius, borderWidth, dataKeys, getColor, theme, reverseTrendGroup],
  );

  const toggleLabels = useCallback(() => {
    setIsShowLabels((prevState) => !prevState);
  }, []);

  const { xScale, yScale } = useMemo(() => {
    const dataXRange = d3.extent(data, (d) => d.x);
    const dataYRange = d3.extent(data, (d) => d.y);

    const rangeMarginX = 0.05 * Math.abs(dataXRange[0] - dataXRange[1]) || 1;
    const rangeMarginY = 0.1 * Math.abs(dataYRange[0] - dataXRange[1]) || 1;

    dataXRange[0] -= rangeMarginX;
    dataXRange[1] += rangeMarginX;
    dataYRange[0] -= rangeMarginY;
    dataYRange[1] += rangeMarginY;

    const xScale = d3.scaleLinear().domain(dataXRange).range([0, width]);
    const yScale = d3.scaleLinear().domain(dataYRange).range([height, 0]);

    return { xScale, yScale };
  }, [width, data, height]);

  const { xAxis, yAxis } = useMemo(() => {
    const xAxis = d3.axisTop(xScale).ticks(10, 'r').tickSize(-height);
    const yAxis = d3.axisLeft(yScale).ticks(10, 'r').tickSize(-width);

    return { xAxis, yAxis };
  }, [width, height, xScale, yScale]);

  const setupDimensions = useCallback(() => {
    const wrapper = chartRef.current;
    const wrapperWidth = wrapper.clientWidth;

    const root = d3.select(wrapper);

    const width = chartRef.current?.clientWidth - CHART_MARGIN.left - CHART_MARGIN.right;

    svgRef.current = root.html('').append('svg').attr('width', wrapperWidth).attr('height', wrapperHeight);
    svgRef.current
      .append('defs')
      .append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('width', width)
      .attr('height', height);

    setChartWidth(width);
  }, [height]);

  const setupAxesLabels = useCallback(() => {
    if (xLabel) {
      svgRef.current
        .append('text')
        .attr('transform', 'translate(' + (width / 2 + 20) + ' ,' + (height + CHART_MARGIN.top + 25) + ')')
        .style('text-anchor', 'middle')
        .text(xLabel)
        .classed(scss.axisLabel, true);
    }

    if (yLabel) {
      svgRef.current
        .append('text')
        .attr('transform', 'translate(' + (width + CHART_MARGIN.left + 25) + ' ,' + (height / 2 + 20) + ')rotate(90)')
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .text(yLabel)
        .classed(scss.axisLabel, true);
    }
  }, [width, height, xLabel, yLabel]);

  const setupAxes = useCallback(() => {
    svgRef.current
      .append('g')
      .attr('transform', 'translate(' + CHART_MARGIN.left + ',' + CHART_MARGIN.top + ')')
      .attr('class', 'x-axis')
      .call(xAxis);

    svgRef.current
      .append('g')
      .attr('transform', 'translate(' + CHART_MARGIN.left + ',' + CHART_MARGIN.top + ')')
      .attr('class', 'y-axis')
      .call(yAxis);

    setupAxesLabels();
  }, [setupAxesLabels, xAxis, yAxis]);

  const displayContextMenu = (event) => {
    const { domain } = event?.srcElement?.__data__ || {};

    if (domain) {
      contextMenu.show({
        id: SCATTER_CONTEXT_MENU,
        event,
        props: {
          domain,
        },
      });
    }
  };

  const setupDataPoints = useCallback(() => {
    const points_g = svgRef.current
      .append('g')
      .attr('transform', 'translate(' + CHART_MARGIN.left + ',' + CHART_MARGIN.top + ')')
      .attr('clip-path', 'url(#clip)')
      .classed('points_g', true);

    points_g
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', function (d) {
        return xScale(d.x);
      })
      .attr('cy', function (d) {
        return yScale(d.y);
      })
      .attr('r', function (d) {
        return d.radius;
      })
      .attr('fill', function (d) {
        return d.backgroundColor;
      })
      .attr('stroke', function (d) {
        return d.borderColor || '';
      })
      .attr('stroke-width', function (d) {
        return d.borderWidth;
      })
      .on('contextmenu', displayContextMenu)
      .style('cursor', 'pointer');

    if (isShowLabels) {
      points_g
        .selectAll('text')
        .data(data)
        .enter()
        .append('text')
        .attr('x', function (d) {
          return xScale(d.x) + d.radius + labelMarginX;
        })
        .attr('y', function (d) {
          return yScale(d.y) + d.radius + labelMarginY;
        })
        .text(function (d) {
          return d.label;
        })
        .attr('font-size', '10px')
        .attr('fill', function (d) {
          return d.labelColor ? d.labelColor : '';
        });
    }
  }, [data, isShowLabels, xScale, yScale]);

  const createTooltipContent = useCallback(
    (itemData) => {
      const { label, x, y, xTrend, yTrend, backgroundColor, borderColor, companyName, domain } =
        itemData?.srcElement?.__data__ || {};
      const records = [];

      const tooltipTitle = label || companyName || domain;
      const tooltipSubtitle = companyName ? domain : '';

      if (xLabel) {
        records.push({ name: xLabel, value: x });
        if (xTrend !== undefined && !isNaN(xTrend)) {
          records.push({ name: `${xLabel} Trend`, value: xTrend });
        }
      }
      if (yLabel) {
        records.push({ name: yLabel, value: y });
        if (yTrend !== undefined && !isNaN(yTrend)) {
          records.push({ name: `${yLabel} Trend`, value: yTrend });
        }
      }
      return createInfoboxMarkup({
        title: tooltipTitle,
        subTitle: tooltipSubtitle,
        marker: { color: backgroundColor, borderColor },
        records,
      });
    },
    [xLabel, yLabel],
  );

  const setupTooltip = useCallback(() => {
    const points = svgRef.current.selectAll('.points_g circle');

    const tip = d3
      .select(chartRef.current)
      .append('div')
      .attr('class', scss.tooltip)
      .style('position', 'fixed')
      .style('opacity', 0);

    points
      .on('mouseover', (d) => {
        const content = createTooltipContent(d);
        tip.html(content);

        const place = getTipCoordinates(d, tip.node(), chartRef.current);
        tip
          .transition()
          .duration(200)
          .style('opacity', 0.9)
          .style('left', place.x + 'px')
          .style('top', place.y + 'px');
      })
      .on('mouseout', () => {
        tip.transition().duration(500).style('opacity', 0);
      });
  }, [createTooltipContent]);

  const zoomIn = useCallback(() => {
    if (zoomRef.current) {
      svgRef.current.style('pointer-events', 'all').transition().call(zoomRef.current.scaleBy, 1.25);
    }
  }, []);

  const resetZoom = useCallback(() => {
    if (zoomRef.current) {
      svgRef.current.style('pointer-events', 'all').transition().call(zoomRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  const zoomOut = useCallback(() => {
    if (zoomRef.current) {
      svgRef.current.style('pointer-events', 'all').transition().call(zoomRef.current.scaleBy, 0.8);
    }
  }, []);

  const handleZoomStart = () => {
    svgRef.current.style('cursor', 'grabbing');
  };

  const handleZoomEnd = () => {
    svgRef.current.style('cursor', 'grab');
  };

  const handleZoom = useCallback(
    (event) => {
      const { transform } = event;
      zoomTransformRef.current = transform;

      const newXScale = transform.rescaleX(xScale);
      const newYScale = transform.rescaleY(yScale);

      svgRef.current.select('.x-axis').call(xAxis.scale(newXScale));
      svgRef.current.select('.y-axis').call(yAxis.scale(newYScale));

      svgRef.current
        .selectAll('.points_g circle')
        .data(data)
        .attr('cx', function (d) {
          return newXScale(d.x);
        })
        .attr('cy', function (d) {
          return newYScale(d.y);
        });

      svgRef.current
        .selectAll('.points_g text')
        ?.data(data)
        .attr('x', function (d) {
          return newXScale(d.x) + d.radius + labelMarginX;
        })
        .attr('y', function (d) {
          return newYScale(d.y) + d.radius + labelMarginY;
        });
    },
    [data, xAxis, xScale, yAxis, yScale],
  );

  const switchOnZoom = useCallback(() => {
    if (zoomRef.current) {
      svgRef.current.call(zoomRef.current).call(zoomRef.current.transform, zoomTransformRef.current || d3.zoomIdentity);
    }
  }, []);

  const initializeZoom = useCallback(() => {
    zoomRef.current = d3.zoom().on('zoom', handleZoom).on('start', handleZoomStart).on('end', handleZoomEnd);
  }, [handleZoom]);

  const setupZoom = useCallback(() => {
    if (zoomRef.current && isZoomEnabled) {
      svgRef.current.call(zoomRef.current).call(zoomRef.current.transform, zoomTransformRef.current || d3.zoomIdentity);
    }
  }, [isZoomEnabled]);

  const renderChart = useCallback(() => {
    setupDimensions();

    if (width) {
      setupAxes();
      setupDataPoints();
      setupTooltip();
      initializeZoom();
      setupZoom();
    }
  }, [setupDimensions, setupAxes, setupDataPoints, setupTooltip, initializeZoom, setupZoom, width]);

  useEffect(() => {
    renderChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isShowLabels, width]);

  // RERENDER CHART ON RESIZE
  useEffect(() => {
    window.addEventListener('resize', renderChart);

    return () => {
      window.removeEventListener('resize', renderChart);
    };
  }, [renderChart]);

  // ENABLE ZOOM
  useEffect(() => {
    const handleClick = () => {
      setIsZoomEnabled(true);
      switchOnZoom();
    };

    if (chartRef.current) {
      chartRef.current.addEventListener('click', handleClick);
    }
  }, [switchOnZoom]);

  return (
    <div className={scss.chart}>
      <Toolbar
        buttons={[
          { text: 'reset', handler: resetZoom },
          { text: 'zoomIn', handler: zoomIn },
          { text: 'zoomOut', handler: zoomOut },
          {
            text: isShowLabels ? 'hideLabels' : 'showLabels',
            handler: toggleLabels,
          },
        ]}
      />
      <div>
        <div
          ref={chartRef}
          style={{
            height: `${wrapperHeight}px`,
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>
      <VisitWebsiteContextMenu id={SCATTER_CONTEXT_MENU} />
    </div>
  );
};

export default ScatterChart;
